<script setup lang="ts">
/* Portage de app/components/AppHeader.vue : logo Arkyan + « Hello, Prénom »,
 * navigation filtrée par rôle (spec §9.1) et, à droite, un DropdownMenu sur
 * l'avatar (Mon profil / Thème / Se déconnecter).
 *
 * Écarts assumés par rapport au Nuxt :
 *  - les boutons thème et déconnexion passent dans le menu de l'avatar ;
 *  - le rôle vient du store (donc de GET /api/me), plus des claims du jeton ;
 *  - `/garde` n'est pas porté (décision verrouillée).
 */
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { LogOut, Moon, Sun, User } from 'lucide-vue-next';
import { Theme } from '@/shared/enums';
import { useAuthStore } from '@/auth/authStore';
import { useTheme } from '@/composables/useTheme';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const auth = useAuthStore();
const { theme, toggleTheme } = useTheme();
const route = useRoute();

interface NavItem {
  to: string;
  label: string;
  show: boolean;
}

const navItems = computed<NavItem[]>(() => {
  const isClient = auth.isClient;
  return [
    { to: '/issues', label: 'Tickets', show: isClient },
    { to: '/tickets', label: 'Tickets', show: !isClient },
    { to: '/suivi-projet', label: 'Suivi de projet', show: true },
    { to: '/backoffice', label: 'Back office', show: auth.isAdmin },
    { to: '/gitlab', label: 'Tableau GitLab', show: !isClient },
    { to: '/timesheet', label: 'Timesheet', show: !isClient },
    { to: '/links', label: 'Liens', show: true },
  ].filter((item) => item.show);
});

const themeLabel = computed(() => (theme.value === Theme.Doux
  ? 'Thème : passer en clair'
  : 'Thème : passer en doux'));

function isActive(to: string): boolean {
  if (to === '/') return route.path === '/';
  return route.path === to || route.path.startsWith(`${to}/`);
}

function onThemeSelect(event: Event): void {
  // Le menu reste ouvert : on voit le thème basculer sous ses yeux.
  event.preventDefault();
  toggleTheme();
}

function onSignOut(): void {
  void auth.signOut();
}
</script>

<template>
  <header class="pnav">
    <div class="pnav-inner">
      <div class="pnav-left">
        <RouterLink to="/" class="logo" aria-label="Accueil Arkyan">
          <span class="logo-mark">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 526 478"
              width="28"
              height="28"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M271.18,98.72c14.8,0,27.06.88,34.83,1.65,26.13,2.39,50.86,10.68,69.26,23.32,20.23,13.77,31.33,31.47,32.17,51.42,1.23,30.99-25.24,55.53-56.51,67.99-8.13,3.24-16.59,5.66-24.97,7.17-2.95.56-6.46.98-11.66,1.83-15.31,2.11-36.39,5.34-56.33,11.94h-.42l.56.14c-13.07,4.36-23.04,10.26-26.97,14.05-1.4,1.26-1.69,2.39-1.4,3.37.28.98,1.4,1.69,2.39,1.69h1.12c1.32.1,2.67.15,4.04.15,10.16,0,22.01-2.65,39.08-6.61,7.34-1.73,15.29-2.56,23.14-2.56,10.03,0,19.88,1.36,28,3.96,14.47,4.78,24.58,9.41,35.54,20.37,16.02,17.98,24.87,44.95,28.66,59.42l.28.98h-65.47v-.42c-3.37-9.83-6.88-16.72-10.82-21.63-2.39-2.81-4.64-4.36-7.02-5.2-1.59-.53-3.5-.81-5.7-.81-2.46,0-5.3.35-8.49,1.09-22.71,5.23-41.49,7.85-56.94,7.85-2.81,0-5.51-.09-8.1-.26-13.21-.7-23.88-3.65-32.73-8.85-12.5-7.16-21.07-18.82-24.16-32.59-5.06-22.62,1.69-44.11,19.11-60.83,11.38-10.82,27.11-19.53,47.76-26.55,18.9-6.27,39.62-10.28,55.05-12.82,6.01-.99,11.22-1.76,15.2-2.35h.7c2.53-.42,4.5-.7,5.62-.84,17.56-3.23,20.93-13.91,20.93-17.28-.28-2.53-1.83-5.06-4.92-7.59-3.79-2.81-12.78-8.15-31.61-9.97-9.47-.88-19.01-1.31-28.41-1.31-18.19,0-35.86,1.61-51.45,4.7-7.33,1.45-14.2,3.23-20.45,5.32-27.53,9.41-40.32,22.48-40.32,41.58v149.05h-61.25v-149.05c0-28.01,10.71-48.74,25.26-63.99,17.34-18.17,40.13-28.56,56.79-34.21,31.17-10.63,64.78-13.31,90.62-13.31" />
            </svg>
          </span>
          <span class="logo-greet">
            Hello, <b>{{ auth.firstName || 'invité' }}</b>
          </span>
        </RouterLink>

        <nav class="pnav-links">
          <RouterLink
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            :class="['pnav-link', isActive(item.to) ? 'on' : '']"
          >
            {{ item.label }}
          </RouterLink>
        </nav>
      </div>

      <div class="pnav-right">
        <span class="pnav-user">
          <span class="pnav-name">{{ auth.displayName }}</span>
          <span v-if="!auth.isClient && auth.me" class="pnav-mail">{{ auth.me.email }}</span>
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              class="pnav-avatar"
              :aria-label="`Connecté en tant que ${auth.displayName}`"
            >
              {{ auth.initials }}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="min-w-52">
            <DropdownMenuLabel class="truncate">
              {{ auth.displayName }}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem v-if="!auth.isClient" asChild>
              <RouterLink to="/profile" class="w-full cursor-pointer">
                <User class="size-4" aria-hidden="true" />
                Mon profil
              </RouterLink>
            </DropdownMenuItem>
            <DropdownMenuItem class="cursor-pointer" @select="onThemeSelect">
              <Sun v-if="theme === Theme.Doux" class="size-4" aria-hidden="true" />
              <Moon v-else class="size-4" aria-hidden="true" />
              {{ themeLabel }}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem class="cursor-pointer" @select="onSignOut">
              <LogOut class="size-4" aria-hidden="true" />
              Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  </header>
</template>

<style scoped>
/* Styles repris tels quels de app/components/AppHeader.vue. */
.pnav {
  background: var(--bg-1);
  border-bottom: 1px solid var(--line);
  font-family: var(--sans);
}
.pnav-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 14px 24px;
}
.pnav-left {
  display: flex;
  align-items: center;
  gap: 32px;
  min-width: 0;
}
.logo {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
}
.logo-mark {
  display: inline-flex;
  color: var(--accent);
}
.logo-greet {
  font-size: 14px;
  color: var(--text-2);
  letter-spacing: -0.005em;
}
.logo-greet b {
  color: var(--text);
  font-weight: 500;
}

.pnav-links {
  display: flex;
  gap: 24px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-2);
  flex-wrap: wrap;
}
.pnav-link {
  padding: 6px 2px;
  border-bottom: 1.5px solid transparent;
  transition: color 0.15s, border-color 0.15s;
}
.pnav-link:hover {
  color: var(--text);
}
.pnav-link.on {
  color: var(--text);
  border-bottom-color: var(--accent);
}

.pnav-right {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pnav-user {
  display: flex;
  flex-direction: column;
  text-align: right;
  font-size: 12px;
  line-height: 1.3;
}
.pnav-name {
  color: var(--text);
  font-weight: 500;
}
.pnav-mail {
  color: var(--text-3);
  font-size: 11px;
}

.pnav-avatar {
  width: 30px;
  height: 30px;
  border-radius: var(--radius);
  background: var(--accent-soft);
  border: 1px solid var(--accent-dim);
  color: var(--accent-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-family: var(--mono);
  font-weight: 600;
  flex-shrink: 0;
  transition: filter 0.15s;
}
.pnav-avatar:hover {
  filter: brightness(1.1);
}
</style>
